import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Grid } from "@mui/material";
import BasicDialog from "../../base/BasicDialog";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import useUserService from "../../../services/userService";
import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import { useForm } from "../../../hooks/useForm";
import { useCallback, useEffect } from "react";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSnackbarAlert } from "../../../context/snackbarAlert";
import { getErrorMsg, isEmptyObject, isEmptyValue } from "../../../helpers/methods";
import useOwnerFormsService from "../../../hooks/useOwnerFormsService";
import IbanFormBox from "../../box/IbanFormBox";
import IbanDialog from "../IbanDialog";
import TextFieldFormControl from "../../field/TextFieldFormControl";
import TrueFalseSelectField from "../../field/TrueFalseSelectField";


const ExternalOwnerDialog = (props) => {
  const { t } = useTranslation();
  const snackbarAlert = useSnackbarAlert();

  const {
    formValue,
    setFormValue,
    onChange,
  } = useForm({ owner_active: true, owner_external : true});


  const ownerDetailsData = useAsync(
    () => getOwnerDetails(props.ownerId),
    [props.ownerId]
  );

  const ownerFilteringData = useAsync(
    () => getOwnersFilteringData(),
    []
  );


  useEffect(() => {
    if (ownerDetailsData.loading) {
      return;
    }
    if (!isEmptyObject(ownerDetailsData.value)) {
      setFormValue(ownerDetailsData.value);
    }

  }, [ownerDetailsData.loading]);


  const {
    openIbanDialog,
    onOpenIbanDialog,
    selectedIbanId,
    onAddIbanForNewOwner,
    onEditIban,
    onRemoveIban,
    onCloseIbanDialog,
  } =
    useOwnerFormsService(ownerDetailsData.refetch,
      formValue,
      setFormValue,
      props.ownerId)


  const {
    getOwnerDetails,
    updateOwnerDetails,
    createOwner,
    getOwnersFilteringData,
    c} = useUserService();

  const updateOwnerDetailsFn = useAsyncFn(updateOwnerDetails)
  const createOwnerFn = useAsyncFn(createOwner)

 

  const onSubmit = useCallback(() => {
    if (props.ownerId) {
      updateOwnerDetailsFn
        .execute(props.ownerId, formValue)
        .then((res) => {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.external_owner_updated")
          );
          props.onClose();
        })
        .catch((err) => {
          snackbarAlert.openErrorSnackbarAlert(
            getErrorMsg(err.data),
            t("snackbar_alert.occurred_error_during_external_owner_updating")
          );
        });
    } else {

      createOwnerFn
        .execute(formValue)
        .then((res) => {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.external_owner_created")
          );
          props.onClose();
        })
        .catch((err) => {
          snackbarAlert.openErrorSnackbarAlert(
            getErrorMsg(err.data),
            t("snackbar_alert.occurred_error_during_external_owner_creating")
          );
        });
    }

  }, [formValue]);



  const isReadOnly = !formValue?.owner_active || props.readOnly

  const isValid = !isEmptyValue(formValue.owner_full_name) && !isEmptyValue(formValue.owner_short_name)

  return (
    <BasicDialog
      open={props.open}
      onClose={props.onClose}
      titleAlign="center"
      contentAlign="center"
      title={props.ownerId ? t(`dialog.owner_details.owner_details`) : t(`dialog.owner_details.create_owner`)}
      maxWidth="xl"
      showDialogActions
    >

      <LoaderWrapper showLoader={ownerDetailsData.loading || ownerFilteringData.loading} >
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
            <Grid item xs={4}>
              <TextFieldFormControl
                name="owner_full_name"
                readOnly={props.readOnly}
                value={formValue?.owner_full_name}
                label={t("dialog.owner_details.owner_full_name")}
                onChange={onChange}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <TextFieldFormControl
                name="owner_short_name"
                readOnly={props.readOnly}
                value={formValue?.owner_short_name}
                label={t("dialog.owner_details.owner_short_name")}
                onChange={onChange}
                required   
              /> 
            </Grid>      
            <Grid item xs={4}>
                    <TrueFalseSelectField
                      name="owner_active"
                      readOnly={props.readOnly}
                      valuesAsBool
                      value={formValue?.owner_active}
                      label={t("dialog.owner_details.owner_active")}
                      onChange={onChange}
                      addEmptyOptions={false}
                    />
                  </Grid>
          <Grid item xs={12}>
            <IbanFormBox
              ibans={formValue?.ibans}
              onAddIban={onOpenIbanDialog}
              onEditIban={onEditIban}
              onRemoveIban={onRemoveIban}
              readOnly={isReadOnly}
              ownerId={props.ownerId}
            />
          </Grid>


          {( !props.readOnly) &&
            <Grid item xs={12}>
              <LoadingButton
                variant="contained"
                color="primary"
                fullWidth
                loading={updateOwnerDetailsFn.loading}
                disabled={!isValid}
                onClick={onSubmit}
              >
                {props.ownerId ? t("save") : t("create")}
              </LoadingButton>
            </Grid>
          }
        </Grid>

      </LoaderWrapper>


      {!props.readOnly && openIbanDialog &&
        <IbanDialog
          ibanId={selectedIbanId.current}
          open={openIbanDialog}
          ownerId={props.ownerId}
          onRefetch={ownerDetailsData.refetch}
          onClose={onCloseIbanDialog}
          onAddIbanForNewOwner={onAddIbanForNewOwner}
        />
      }

    </BasicDialog>
  );
};

ExternalOwnerDialog.propTypes = {
  ownerId: PropTypes.string,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  readOnly: PropTypes.bool
};

ExternalOwnerDialog.defaultProps = {
  open: false,
  readOnly: true
};

export default ExternalOwnerDialog;
